import { useRef, useState, useCallback } from 'react';
import type { EnrichedSignal } from '../types/signals';

const DEBOUNCE_MS = 300;
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8001';

export interface UseLazyStrategyBreakdownResult {
  breakdown: EnrichedSignal | null;
  isLoading: boolean;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
}

export function useLazyStrategyBreakdown(
  symbol: string,
  enabled: boolean // false for top-15 rows and non-v2 users
): UseLazyStrategyBreakdownResult {
  const [breakdown, setBreakdown] = useState<EnrichedSignal | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // fetchedRef tracks whether we have already fetched for this symbol in this mount
  const fetchedRef = useRef(false);

  const handleMouseEnter = useCallback(() => {
    if (!enabled) return;
    if (fetchedRef.current) return; // local cache hit — already fetched

    debounceRef.current = setTimeout(async () => {
      abortRef.current = new AbortController();
      setIsLoading(true);

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${API_BASE}/api/v1/signals/${encodeURIComponent(symbol)}/strategy-breakdown`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            signal: abortRef.current.signal,
          }
        );

        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data: EnrichedSignal = await response.json();
        fetchedRef.current = true;
        setBreakdown(data);
      } catch (err: unknown) {
        // AbortError is expected when mouse leaves before response — not a real error
        if (err instanceof Error && err.name !== 'AbortError') {
          console.warn(`[useLazyStrategyBreakdown] fetch failed for ${symbol}:`, err.message);
        }
      } finally {
        setIsLoading(false);
      }
    }, DEBOUNCE_MS);
  }, [symbol, enabled]);

  const handleMouseLeave = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
      setIsLoading(false);
    }
  }, []);

  return { breakdown, isLoading, handleMouseEnter, handleMouseLeave };
}
